/**
 * A photo straight off a phone camera is a handful of megabytes of detail
 * nobody ever sees at the size a handshake shows it, and on a spotty signal
 * that is the difference between a wait and no wait at all.
 */
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.8;

/** The size to draw at, keeping the shape of the original. */
export function clampDimensions(
  width: number,
  height: number,
  max: number = MAX_DIMENSION,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (!longest || longest <= max) {
    return { width, height };
  }

  const scale = max / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image.'));
    };

    image.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

/**
 * Redraws a photo down to something worth sending. Anything that goes wrong on
 * the way — an image the browser cannot decode, a canvas it will not hand back
 * — leaves the original file untouched, since a big upload beats no upload.
 */
export async function compressImage(
  file: File,
  max: number = MAX_DIMENSION,
  quality: number = JPEG_QUALITY,
): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') {
    return file;
  }

  try {
    const image = await loadImage(file);
    const { width, height } = clampDimensions(image.naturalWidth, image.naturalHeight, max);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) return file;

    context.drawImage(image, 0, 0, width, height);

    const blob = await toBlob(canvas, quality);
    if (!blob || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, '') || 'photo';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}
